import React, { useRef, useState } from 'react';
import { ErrorMessage } from 'formik';

const stripPrefix = (value, prefix) => {
	if (!prefix || !value) return value || '';
	return value.startsWith(prefix) ? value.slice(prefix.length) : value;
};

const withPrefix = (value, prefix) => {
	const trimmed = (value || '').trim();
	if (!trimmed) return '';
	if (!prefix) return trimmed;
	return trimmed.startsWith(prefix) ? trimmed : `${prefix}${trimmed}`;
};

const splitPastedItems = text =>
	text
		.split(/[\r\n•]+/)
		.map(item => item.trim())
		.filter(Boolean);

export default function MultiItemsList({
	items,
	push,
	remove,
	replace,
	placeholder = '',
	inputPrefix = '',
	errorName
}) {
	const [draftValue, setDraftValue] = useState('');
	const [editingIndex, setEditingIndex] = useState(null);
	const [editingValue, setEditingValue] = useState('');
	const editInputRef = useRef(null);

	const startEdit = idx => {
		setEditingIndex(idx);
		setEditingValue(stripPrefix(items[idx], inputPrefix));
		setTimeout(() => editInputRef.current?.focus(), 0);
	};

	const cancelEdit = () => {
		setEditingIndex(null);
		setEditingValue('');
	};

	const saveEdit = () => {
		const nextValue = withPrefix(editingValue, inputPrefix);
		if (nextValue) {
			replace(editingIndex, nextValue);
		} else {
			remove(editingIndex);
		}
		cancelEdit();
	};

	const handleAddClick = () => {
		const nextValue = withPrefix(draftValue, inputPrefix);
		if (nextValue) {
			push(nextValue);
			setDraftValue('');
		}
	};

	const handlePaste = event => {
		event.preventDefault();
		const pastedValues = splitPastedItems(event.clipboardData.getData('text'));

		if (pastedValues.length > 1) {
			pastedValues.forEach(item => {
				const nextValue = withPrefix(item, inputPrefix);
				if (nextValue) push(nextValue);
			});
			setDraftValue('');
			return;
		}

		setDraftValue(stripPrefix(pastedValues[0] || '', inputPrefix));
	};

	const hasPendingInput = Boolean(draftValue?.trim());

	const renderEditField = (value, onChange, onSave, onCancel, inputRef) => (
		<div className={`ats-field-group${inputPrefix ? ' has-input-prefix' : ''}`}>
			{inputPrefix ? (
				<span className="ats-input-prefix" aria-hidden="true">
					{inputPrefix}
				</span>
			) : null}
			<input
				ref={inputRef}
				type="text"
				value={value}
				onChange={onChange}
				onKeyDown={e => {
					if (e.key === 'Enter') {
						e.preventDefault();
						onSave();
					} else if (e.key === 'Escape') {
						onCancel();
					}
				}}
				placeholder={placeholder}
				className="ats-text-field"
			/>
			<button
				type="button"
				onClick={onSave}
				className="ats-add-btn-field left button-bg-soft-small is-icon-only">
				<img src="/ats/icons/check-primary-icon.svg" alt="Save" />
			</button>
			<button
				type="button"
				onClick={onCancel}
				className="ats-add-btn-field remove-btn button-border-soft-small is-icon-only">
				<img src="/ats/icons/close-blue-icon.svg" alt="Cancel" />
			</button>
		</div>
	);

	return (
		<div className="is-flex is-flex-direction-column gap-3">
			{items.map((item, idx) => {
				if (editingIndex === idx) {
					return (
						<div key={idx}>
							{renderEditField(
								editingValue,
								e => setEditingValue(e.target.value),
								saveEdit,
								cancelEdit,
								editInputRef
							)}
						</div>
					);
				}

				return (
					<div key={idx} className="field-array-item">
						<p>{item}</p>
						<button
							type="button"
							onClick={() => startEdit(idx)}
							className="edit-btn"
							title="Edit">
							<img src="/ats/icons/pencil-blue-icon.svg" alt="Edit" />
						</button>
						<button
							type="button"
							onClick={() => remove(idx)}
							className="remove-btn"
							title="Remove">
							✕
						</button>
					</div>
				);
			})}

			{errorName && (
				<ErrorMessage
					name={errorName}
					component="div"
					className="text-red-600 text-sm"
				/>
			)}

			<div
				className={`ats-field-group${hasPendingInput ? ' has-save-btn' : ''}${
					inputPrefix ? ' has-input-prefix' : ''
				}`}>
				{inputPrefix ? (
					<span className="ats-input-prefix" aria-hidden="true">
						{inputPrefix}
					</span>
				) : null}
				<input
					type="text"
					value={draftValue}
					onChange={e => setDraftValue(e.target.value)}
					placeholder={placeholder}
					className="ats-text-field"
					onPaste={handlePaste}
					onKeyDown={e => {
						if (e.key === 'Enter' && hasPendingInput) {
							e.preventDefault();
							handleAddClick();
						}
					}}
				/>
				<button
					type="button"
					onClick={handleAddClick}
					className="ats-add-btn-field ats-save-btn-field ats-button-primary"
					tabIndex={hasPendingInput ? 0 : -1}
					aria-hidden={!hasPendingInput}
					disabled={!hasPendingInput}>
					Save
				</button>
			</div>
		</div>
	);
}
